'use client'

import { cn } from '@/lib/utils'
import { ContentBlockStyle } from '../types'

interface StyleOption {
  value: ContentBlockStyle
  label: string
}

interface StyleSelectorProps {
  options: StyleOption[]
  value: string | undefined
  onChange: (value: string) => void
}

export function StyleSelector({ options, value, onChange }: StyleSelectorProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            'flex flex-col items-center gap-2 rounded-lg p-3 transition-all',
            value === option.value
              ? 'bg-primary/10'
              : 'bg-transparent hover:bg-gray-50'
          )}
        >
          <StyleMockup style={option.value} />
          <span className="text-xs font-medium text-gray-700">{option.label}</span>
        </button>
      ))}
    </div>
  )
}

function StyleMockup({ style }: { style: ContentBlockStyle }) {
  const mockupClass = 'w-full h-16 rounded border border-gray-200 bg-gray-50 p-1.5 overflow-hidden'

  switch (style) {
    // Text & Image styles
    case 'side_by_side':
      return (
        <div className={mockupClass}>
          <div className="flex h-full gap-1">
            {/* Text side */}
            <div className="flex w-1/2 flex-col justify-center gap-0.5 px-1">
              <div className="h-1.5 w-3/4 rounded-sm bg-gray-400" />
              <div className="h-1 w-full rounded-sm bg-gray-300" />
              <div className="h-1 w-2/3 rounded-sm bg-gray-300" />
            </div>
            {/* Image side */}
            <div className="w-1/2 rounded bg-gray-300" />
          </div>
        </div>
      )

    case 'image_left':
      return (
        <div className={mockupClass}>
          <div className="flex h-full flex-row-reverse gap-1">
            {/* Text side */}
            <div className="flex w-1/2 flex-col justify-center gap-0.5 px-1">
              <div className="h-1.5 w-3/4 rounded-sm bg-gray-400" />
              <div className="h-1 w-full rounded-sm bg-gray-300" />
              <div className="h-1 w-2/3 rounded-sm bg-gray-300" />
            </div>
            {/* Image side */}
            <div className="w-1/2 rounded bg-gray-300" />
          </div>
        </div>
      )

    case 'text_on_image':
      return (
        <div className={mockupClass}>
          <div className="relative h-full w-full rounded bg-gray-300">
            {/* Centered text overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
              <div className="h-1.5 w-1/2 rounded-sm bg-gray-600" />
              <div className="h-1 w-1/3 rounded-sm bg-gray-500" />
            </div>
          </div>
        </div>
      )

    // Text only styles
    case '1_column_title_top':
      return (
        <div className={mockupClass}>
          <div className="flex h-full flex-col items-center justify-center gap-1">
            <div className="h-1.5 w-1/3 rounded-sm bg-gray-400" />
            <div className="h-1 w-2/3 rounded-sm bg-gray-300" />
            <div className="h-1 w-1/2 rounded-sm bg-gray-300" />
          </div>
        </div>
      )

    case '2_columns_title_top_center':
      return (
        <div className={mockupClass}>
          <div className="flex h-full flex-col gap-1">
            {/* Centered title */}
            <div className="flex justify-center">
              <div className="h-1.5 w-1/4 rounded-sm bg-gray-400" />
            </div>
            {/* Two columns */}
            <div className="flex flex-1 gap-1">
              <div className="flex w-1/2 flex-col gap-0.5">
                <div className="h-1 w-full rounded-sm bg-gray-300" />
                <div className="h-1 w-3/4 rounded-sm bg-gray-300" />
              </div>
              <div className="flex w-1/2 flex-col gap-0.5">
                <div className="h-1 w-full rounded-sm bg-gray-300" />
                <div className="h-1 w-2/3 rounded-sm bg-gray-300" />
              </div>
            </div>
          </div>
        </div>
      )

    case '3_columns_title_left':
      return (
        <div className={mockupClass}>
          <div className="flex h-full gap-1">
            {/* Title column */}
            <div className="flex w-1/3 items-start pt-0.5">
              <div className="h-1.5 w-3/4 rounded-sm bg-gray-400" />
            </div>
            {/* Two text columns */}
            <div className="flex w-2/3 gap-1">
              <div className="flex w-1/2 flex-col gap-0.5">
                <div className="h-1 w-full rounded-sm bg-gray-300" />
                <div className="h-1 w-3/4 rounded-sm bg-gray-300" />
              </div>
              <div className="flex w-1/2 flex-col gap-0.5">
                <div className="h-1 w-full rounded-sm bg-gray-300" />
                <div className="h-1 w-2/3 rounded-sm bg-gray-300" />
              </div>
            </div>
          </div>
        </div>
      )

    case '2_columns_1_3':
      return (
        <div className={mockupClass}>
          <div className="flex h-full gap-1">
            {/* Narrow left column (1/3) */}
            <div className="flex w-1/3 flex-col gap-0.5">
              <div className="h-1 w-full rounded-sm bg-gray-300" />
              <div className="h-1 w-3/4 rounded-sm bg-gray-300" />
            </div>
            {/* Wide right column (2/3) */}
            <div className="flex w-2/3 flex-col gap-0.5">
              <div className="h-1 w-full rounded-sm bg-gray-300" />
              <div className="h-1 w-full rounded-sm bg-gray-300" />
              <div className="h-1 w-2/3 rounded-sm bg-gray-300" />
            </div>
          </div>
        </div>
      )

    // Gallery styles
    case 'masonry':
      return (
        <div className={mockupClass}>
          <div className="flex h-full gap-0.5">
            <div className="w-1/2 rounded-sm bg-gray-300" />
            <div className="flex w-1/2 flex-col gap-0.5">
              <div className="h-1/2 rounded-sm bg-gray-300" />
              <div className="h-1/2 rounded-sm bg-gray-300" />
            </div>
          </div>
        </div>
      )

    case 'spread_harmony':
      return (
        <div className={mockupClass}>
          <div className="flex h-full gap-0.5">
            {/* Left: tall image */}
            <div className="w-1/2 rounded-sm bg-gray-300" />
            {/* Right: 2x2 grid with diagonal images */}
            <div className="grid w-1/2 grid-cols-2 grid-rows-2 gap-0.5">
              <div className="rounded-sm bg-gray-300" />
              <div className="rounded-sm bg-transparent" />
              <div className="rounded-sm bg-transparent" />
              <div className="rounded-sm bg-gray-300" />
            </div>
          </div>
        </div>
      )

    case 'scroll_rhythm':
      return (
        <div className={mockupClass}>
          <div className="flex h-full items-start gap-0.5">
            <div className="h-full w-1/2 rounded-sm bg-gray-300" />
            <div className="h-2/3 w-1/4 rounded-sm bg-gray-300" />
            <div className="h-2/3 w-1/4 rounded-sm bg-gray-300" />
          </div>
        </div>
      )

    case 'gallery_side_by_side':
      return (
        <div className={mockupClass}>
          <div className="flex h-full gap-0.5">
            <div className="h-full w-1/3 rounded-sm bg-gray-300" />
            <div className="h-full w-1/3 rounded-sm bg-gray-300" />
            <div className="h-full w-1/3 rounded-sm bg-gray-300" />
          </div>
        </div>
      )

    case 'outline':
      return (
        <div className={mockupClass}>
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-1.5 border border-gray-400 rounded-full px-4 py-1">
              <div className="w-10 h-1.5 bg-gray-400 rounded-full" />
              <div className="w-1.5 h-1.5 text-gray-400">→</div>
            </div>
          </div>
        </div>
      )

    case 'filled':
      return (
        <div className={mockupClass}>
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-1.5 bg-gray-400 rounded-full px-4 py-1">
              <div className="w-10 h-1.5 bg-white rounded-full" />
              <div className="w-1.5 h-1.5 text-white">→</div>
            </div>
          </div>
        </div>
      )

    default:
      return null
  }
}
